import type ActionConfirm from "@components/Atoms/ActionConfirm";
import MimeTypes from "@core-ui/ApiServices/Types/MimeTypes";
import ModalToOpenService from "@core-ui/ContextServices/ModalToOpenService/ModalToOpenService";
import ModalToOpen from "@core-ui/ContextServices/ModalToOpenService/model/ModalsToOpen";
import Workspace from "@core-ui/ContextServices/Workspace";
import { useDeferApi } from "@core-ui/hooks/useApi";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import type { CatalogView } from "@ext/catalog/views/models/CatalogViews";
import t from "@ext/localization/locale/translate";
import PermissionService from "@ext/security/logic/Permission/components/PermissionService";
import { editCatalogPermission } from "@ext/security/logic/Permission/Permissions";
import { type ComponentProps, useCallback, useEffect, useRef, useState } from "react";

const PAGE_SIZE = 10;

type CatalogViewsResponse = { views: CatalogView[]; hasMore: boolean };

export const useCatalogViewsAccess = () => {
	const { isTauri, isWeb } = usePlatform();
	const workspace = Workspace.current();
	const { catalogName, isReadOnly, hasViews } = useCatalogPropsStore(
		(state) => ({
			catalogName: state?.data?.name,
			isReadOnly: !!state.data.resolvedVersion,
			hasViews: state.data?.hasViews,
		}),
		"shallow",
	);

	const canEditCatalog = PermissionService.useCheckPermission(editCatalogPermission, workspace.path, catalogName);
	const isEditInstant = isTauri || isWeb;

	return {
		hasViews,
		canEditCatalogViews: canEditCatalog && isEditInstant && !isReadOnly,
		canSeeCatalogViews: !!catalogName && (isEditInstant || hasViews),
	};
};

export const useCatalogViews = (isOpen: boolean) => {
	const [items, setItems] = useState<CatalogView[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const updateProps = useCatalogPropsStore((state) => state.update);

	const offsetRef = useRef(0);
	const hasMoreRef = useRef(true);

	const { call: fetchCatalogViews } = useDeferApi<CatalogViewsResponse, CatalogViewsResponse>({
		parse: "json",
		map: (data) => ({ views: data.views ?? [], hasMore: data.hasMore ?? false }),
	});

	const { call: mutateCatalogView } = useDeferApi<unknown>({
		parse: "json",
		opts: { failOnParse: false },
	});

	const getViews = useCallback(
		async (offset: number) => {
			await fetchCatalogViews({
				url: (api) => api.getCatalogViews(offset, PAGE_SIZE, null),
				onStart: () => setIsLoading(true),
				onDone: (data) => {
					setItems((prev) => (offset ? [...prev, ...data.views] : data.views));
					hasMoreRef.current = data.hasMore;
					offsetRef.current = offset + PAGE_SIZE;
				},
				onFinally: () => setIsLoading(false),
			});
		},
		[fetchCatalogViews],
	);

	useEffect(() => {
		if (!isOpen) return;
		offsetRef.current = 0;
		hasMoreRef.current = true;
		void getViews(0);
	}, [isOpen, getViews]);

	const onLoadMore = useCallback(() => void getViews(offsetRef.current), [getViews]);

	const createView = useCallback(
		async (view: CatalogView) => {
			await mutateCatalogView({
				url: (api) => api.createCatalogView(),
				opts: { body: view, parse: "json", mime: MimeTypes.json },
				onDone: () => {
					setItems((prev) => [...prev, view].sort((a, b) => a.name.localeCompare(b.name)));
					updateProps({ hasViews: true });
				},
			});
		},
		[mutateCatalogView, updateProps],
	);

	const updateView = useCallback(
		async (view: CatalogView) => {
			await mutateCatalogView({
				url: (api) => api.updateCatalogView(),
				opts: { body: view, parse: "json", mime: MimeTypes.json },
				onDone: () =>
					setItems((prev) =>
						prev.map((v) => (v.id === view.id ? view : v)).sort((a, b) => a.name.localeCompare(b.name)),
					),
			});
		},
		[mutateCatalogView],
	);

	const onUpdateDocportalVisible = useCallback(
		async (view: CatalogView, docportalVisible: boolean) => {
			await updateView({ ...view, options: { ...view.options, docportalVisible } });
		},
		[updateView],
	);

	const deleteView = useCallback(
		async (viewId: string) => {
			await mutateCatalogView({
				url: (api) => api.deleteCatalogView(viewId),
				onDone: () => {
					setItems((prev) => {
						const next = prev.filter((v) => v.id !== viewId);
						if (next.length === 0) updateProps({ hasViews: false });
						return next;
					});
				},
			});
		},
		[mutateCatalogView, updateProps],
	);

	const onDeleteClick = useCallback(
		(view: CatalogView) => {
			ModalToOpenService.setValue<ComponentProps<typeof ActionConfirm>>(ModalToOpen.ActionConfirm, {
				confirmTitle: t("confirmation.delete.title"),
				confirmBody: t("catalog.views.edit.deleteDescription"),
				initialIsOpen: true,
				onConfirm: () => deleteView(view.id),
				onClose: () => ModalToOpenService.resetValue(),
			});
		},
		[deleteView],
	);

	return {
		items,
		isLoading,
		hasMoreRef,
		onLoadMore,
		createView,
		updateView,
		onUpdateDocportalVisible,
		onDeleteClick,
	};
};
